import React, { useEffect, useState } from "react";
import YearSelector from "../../Components/YearSelector/YearSelector";
import QuarterSelector from "../../Components/QuarterSelector/QuaterSelector";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { AssignedDto, UserDto } from "../../types/user.dto";
import { Container, Table } from "react-bootstrap";
import { BLANK_STRING, VIEW_MODES } from "../../constants/appConstants";
import LoadingPage from "../../Components/LoadingPage/LoadingPage";
import { getAllUsersThunk } from "../../store/user/thunkMiddlewares";
import UsersYetToPassTable from "../../Components/UsersYetToPassTable/UsersYetToPassTable";
import "./styles.css";
import { useNavigate, useParams } from "react-router-dom";
import { exportQuarterAndYear, getQuarterString } from "../../utils/commonUtils";
import SwitchViewData from "../../Components/SwitchViewData/SwitchViewData";
import MultiQuarterSelector from "../../Components/MultiQuaterSelector/MultiQuaterSelector";
import TableMultipleQuarter from "../../Components/TableMultipleQuarter/TableMultipleQuarter";
import { QuarterAndYear } from "../../types/quarterAndYear";

const DEFAULT_YEAR = new Date().getFullYear();

interface TrainingCompetencyProps {
    isDashboard?: boolean;
}
const TrainingCompetency = (props: TrainingCompetencyProps) => {
    const navigate = useNavigate();
    const { period } = useParams();

    const dispatch = useAppDispatch();
    const userState = useAppSelector((store) => store.user);
    const [selectedYear, setSelectedYear] = useState<number>(DEFAULT_YEAR);
    const [selectedQuarter, setSelectedQuarter] = useState<number>(1);
    const [selectedMultiQuarters, setSelectedMultiQuarters] = useState<QuarterAndYear[]>([]);
    const [assignmentYetToPass, setAssignmentYetToPass] = useState<AssignedDto[]>([]);
    const [currentViewMode, setCurrentViewMode] = useState<string>(VIEW_MODES.DETAILED);
    const [tableMultipleQuarterData, setTableMultipleQuarterData] = useState<any[]>([]);
    const [kpi, setKPI] = useState<{
        total: number;
        passed: number;
        failed: number;
        assigned: number;
    }>({
        total: 0,
        passed: 0,
        failed: 0,
        assigned: 0,
    });
    useEffect(() => {
        !props.isDashboard && dispatch(getAllUsersThunk());
        setCurrentYearAndQuarter();
    }, []);

    const setCurrentYearAndQuarter = () => {
        const { year, quarter } = exportQuarterAndYear(period || BLANK_STRING);
        if (year && quarter) {
            setSelectedYear(year);
            setSelectedQuarter(quarter);
        } else {
            const currentYear = new Date().getFullYear();
            setSelectedYear(currentYear);
            const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
            setSelectedQuarter(currentQuarter);
            const quarterString = getQuarterString(currentQuarter);
            setSelectedMultiQuarters([
                {
                    year: currentYear,
                    quarter: currentQuarter,
                },
            ]);
            !props.isDashboard && navigate(`/training-competency/${quarterString}-${currentYear}`, { replace: true });
        }
    };

    useEffect(() => {
        let allAssignedOfAllUsers: AssignedDto[] = [];
        userState.users.forEach((user: UserDto) => {
            if (user.assigned) {
                allAssignedOfAllUsers = [...allAssignedOfAllUsers, ...user.assigned];
            }
        });
        if (currentViewMode === VIEW_MODES.DETAILED) {
            const { total, passed, failed, assigned, assignedYetToPass } = getDataForQuarter(allAssignedOfAllUsers, {
                year: selectedYear,
                quarter: selectedQuarter,
            });
            setKPI({ total, passed, failed, assigned });
            setAssignmentYetToPass(assignedYetToPass);
        } else {
            let tableData: any = [];
            selectedMultiQuarters
                .sort((a, b) => -1 * (a.year - b.year || a.quarter - b.quarter))
                .forEach((quarterAndYear) => {
                    const { total, passed, failed, assigned } = getDataForQuarter(allAssignedOfAllUsers, quarterAndYear);
                    tableData.push({ total, passed, failed, assigned });
                });
            setTableMultipleQuarterData(tableData);
        }
    }, [userState.users, selectedYear, selectedQuarter, currentViewMode, selectedMultiQuarters]);

    const getDataForQuarter = (allAssignedOfAllUsers: AssignedDto[], quarterAndYear: QuarterAndYear) => {
        const filteredAssigned = allAssignedOfAllUsers.filter((assignment) => {
            const createdAt = new Date(assignment.createdAt);
            const assignmentYear = createdAt.getFullYear();
            const assignmentQuarter = Math.floor(createdAt.getMonth() / 3) + 1;
            return assignmentYear === quarterAndYear.year && assignmentQuarter === quarterAndYear.quarter;
        });

        const { total, passed, failed, assigned, assignedYetToPass } = filteredAssigned.reduce(
            (acc: { total: number; passed: number; failed: number; assigned: number; assignedYetToPass: AssignedDto[] }, assignment) => {
                acc.total++;
                if (assignment.status === "PASSED") {
                    acc.passed++;
                } else if (assignment.status === "FAILED") {
                    acc.assignedYetToPass.push(assignment);
                    acc.failed++;
                } else if (assignment.status === "ASSIGNED") {
                    acc.assignedYetToPass.push(assignment);
                    acc.assigned++;
                }
                return acc;
            },
            { total: 0, passed: 0, failed: 0, assigned: 0, assignedYetToPass: [] }
        );
        return { total, passed, failed, assigned, assignedYetToPass };
    };

    const handleChangeYear = (year: number) => {
        setSelectedYear(year);
        setSelectedQuarter(1);
        !props.isDashboard && navigate(`/training-competency/1st-${year}`, { replace: true });
    };

    const handleChangeQuarter = (quarter: number) => {
        setSelectedQuarter(quarter);
        const quarterString = getQuarterString(quarter);
        !props.isDashboard && navigate(`/training-competency/${quarterString}-${selectedYear}`, { replace: true });
    };

    const renderTrainingCompetency = () => {
        return (
            <>
                <SwitchViewData currentViewMode={currentViewMode} setCurrentViewMode={(view) => setCurrentViewMode(view)} />
                {currentViewMode === VIEW_MODES.DETAILED ? (
                    <div className="training-competency-data">
                        <YearSelector selectedYear={selectedYear} onYearChange={(year) => handleChangeYear(year)} />
                        <QuarterSelector selectedQuarter={selectedQuarter} onQuarterChange={(quarter) => handleChangeQuarter(quarter)} />
                        {renderDetailQuarter()}
                    </div>
                ) : (
                    <div className="training-competency-data">
                        <MultiQuarterSelector
                            selectedMultiQuarters={selectedMultiQuarters}
                            onQuarterChange={(quarters) => setSelectedMultiQuarters(quarters)}
                        />
                        {renderTableMultipleQuarter()}
                    </div>
                )}
            </>
        );
    };

    const renderTrainingCompetencyKPITable = () => {
        const KPI = ((kpi.passed / kpi.total) * 100).toFixed(2);
        return (
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>KPI</th>
                        <th>PASSED</th>
                        <th>FAILED</th>
                        <th>TOTAL</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{KPI}%</td>
                        <td>{kpi.passed}</td>
                        <td>{kpi.total - kpi.passed}</td>
                        <td>{kpi.total}</td>
                    </tr>
                </tbody>
            </Table>
        );
    };

    const renderDetailQuarter = () => {
        return (
            <>
                {renderTrainingCompetencyKPITable()}
                {renderUserYetToPass()}
            </>
        );
    };

    const renderTableMultipleQuarter = () => {
        return selectedMultiQuarters.length ? (
            <TableMultipleQuarter selectedMultiQuarters={selectedMultiQuarters} quarterData={tableMultipleQuarterData} />
        ) : (
            <></>
        );
    };

    const renderUserYetToPass = () => {
        return <UsersYetToPassTable assignmentYetToPass={assignmentYetToPass} />;
    };

    return <Container className="training-competency">{userState.isLoading ? <LoadingPage /> : renderTrainingCompetency()}</Container>;
};

export default TrainingCompetency;
